import { BlockTypeEnum, clientPageTypeEnum } from '../../../core/enums';
import { getUrlPath } from './back-to-list-navigation.helper';
import { ListContextStore, pickListContextEntry } from './list-context.helper';

/** Zapamiętane źródło listy dla nawigacji sąsiadów — żyje w sessionStorage do zamknięcia karty. */
export const NEIGHBOUR_LIST_SOURCE_STORAGE_KEY = 'neighbourListSource';

export interface NeighbourListSourceEntry {
  sourceUrl: string;
  previewUrl: string;
}

/**
 * Typ bloku listy, którego kontekst (filtry/sortowanie/strona) jest właściwy dla
 * nawigacji sąsiadów danego modelu. Współdzielone przez zapis (koniec nawigacji,
 * `NeighbourListNavigationService`) i odczyt (nagłówek do `page-neighbour-models`,
 * `ApiPageService`), żeby obie strony zgadzały się, jakiej listy szukają.
 */
export const NEIGHBOUR_LIST_BLOCK_TYPE_BY_PAGE_TYPE: Partial<Record<clientPageTypeEnum, BlockTypeEnum>> = {
  [clientPageTypeEnum.INVENTORY_WORK_PREVIEW_TYPE]: BlockTypeEnum.INVENTORIES_LIST_TYPE_WWW,
  [clientPageTypeEnum.PRIVATE_VIEW_WORK_PREVIEW_TYPE]: BlockTypeEnum.INVENTORIES_LIST_TYPE_WWW,
  [clientPageTypeEnum.EXHIBITION_WORK_PREVIEW_TYPE]: BlockTypeEnum.INVENTORIES_LIST_TYPE_WWW,
  [clientPageTypeEnum.CONTACT_PREVIEW_TYPE]: BlockTypeEnum.ARTISTS_LIST_TYPE_WWW,
  [clientPageTypeEnum.EXHIBITION_PREVIEW_TYPE]: BlockTypeEnum.EXHIBITIONS_LIST_TYPE,
  [clientPageTypeEnum.NEWS_PREVIEW_TYPE]: BlockTypeEnum.NEWS_LIST_TYPE,
};

export interface ResolveNeighbourListSourceParams {
  sourcePageUrl: string | null;
  targetPageType: clientPageTypeEnum | null | undefined;
  targetUrl: string;
  listContextStore: ListContextStore;
  previousEntry: NeighbourListSourceEntry | null;
}

/**
 * AT-9155: „Powrót do listy" celowo nie pamięta podglądu jako źródła (reguła 2 akcji
 * powrotu) — ale nawigacja sąsiadów potrzebuje odwrotnego zachowania: ma przetrwać
 * właśnie skoki podglądu-na-podgląd (Poprzedni/Następny). Stąd osobna pamięć, oparta
 * na danych (czy źródło ma zapisany kontekst właściwej listy), nie na typach stron:
 *
 * 1. jeśli strona źródłowa ma kontekst listy odpowiedniego typu — to ona jest źródłem
 *    (obejmuje też listę osadzoną w innym podglądzie, np. katalog prac na wystawie);
 * 2. inaczej, jeśli nawigacja kontynuuje poprzedni wpis (jego `previewUrl` to strona,
 *    z której właśnie wychodzimy) i to zapamiętane źródło wciąż ma taki kontekst —
 *    dziedziczymy je (łańcuch Poprzedni/Następny bez utraty listy po pierwszym skoku);
 * 3. inaczej — nie znamy źródła.
 */
export const resolveNeighbourListSource = ({
  sourcePageUrl,
  targetPageType,
  targetUrl,
  listContextStore,
  previousEntry,
}: ResolveNeighbourListSourceParams): NeighbourListSourceEntry | null => {
  const blockType = targetPageType ? NEIGHBOUR_LIST_BLOCK_TYPE_BY_PAGE_TYPE[targetPageType] : null;
  if (!blockType) {
    return null;
  }

  if (sourcePageUrl && pickListContextEntry(listContextStore, sourcePageUrl, blockType)) {
    return {sourceUrl: sourcePageUrl, previewUrl: getUrlPath(targetUrl)};
  }

  // Dziedziczymy tylko przy realnej kontynuacji łańcucha: strona źródłowa tej nawigacji
  // musi być tym samym podglądem, dla którego zapamiętany wpis już obowiązywał — inaczej
  // wejście z zupełnie niepowiązanej strony podstawiłoby stary, przypadkowy kontekst.
  if (
    sourcePageUrl &&
    previousEntry &&
    getUrlPath(previousEntry.previewUrl) === getUrlPath(sourcePageUrl) &&
    pickListContextEntry(listContextStore, previousEntry.sourceUrl, blockType)
  ) {
    return {sourceUrl: previousEntry.sourceUrl, previewUrl: getUrlPath(targetUrl)};
  }

  return null;
};
